/**
 * LandmarkPanel — shown when unlockLandmark phase is active.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 */

import type { Habitat, LandmarkCardDef } from "@cloudnest/cascadia-engine";
import { PlaceholderCard, PlaceholderLandmark } from "./placeholders";

export interface LandmarkPanelProps {
  readonly eligibleHabitats: readonly Habitat[];
  readonly availableCards: readonly LandmarkCardDef[];
  readonly onUnlock: (habitat: Habitat) => void;
  readonly onChooseCard: (cardId: string) => void;
  readonly onSkip: () => void;
  readonly selectedHabitat: Habitat | null;
}

export function LandmarkPanel({
  eligibleHabitats,
  availableCards,
  onUnlock,
  onChooseCard,
  onSkip,
  selectedHabitat,
}: LandmarkPanelProps) {
  const filteredCards = selectedHabitat
    ? availableCards.filter((c) => c.habitat === selectedHabitat)
    : availableCards;

  return (
    <section
      data-testid="cascadia-landmark-panel"
      aria-label="Landmark unlock"
      style={{
        padding: 12,
        background: "var(--cn-cascadia-tile-bg)",
        border: `2px solid var(--cn-cascadia-corridor-active)`,
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--cn-cascadia-corridor-active)",
        }}
      >
        Landmark Unlock — 랜드마크
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {eligibleHabitats.map((habitat) => (
          <button
            key={habitat}
            data-testid={`cascadia-landmark-habitat-${habitat}`}
            data-action-type="UnlockLandmark"
            onClick={() => onUnlock(habitat)}
            type="button"
            aria-pressed={selectedHabitat === habitat}
            style={{
              minHeight: 44,
              padding: "8px 16px",
              borderRadius: 6,
              border:
                selectedHabitat === habitat
                  ? "2px solid var(--cn-cascadia-corridor-active)"
                  : "1px solid var(--cn-cascadia-market-slot-border)",
              background:
                selectedHabitat === habitat
                  ? "var(--cn-cascadia-phase-bg)"
                  : "var(--cn-cascadia-market-slot)",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--cn-cascadia-label)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <svg width={20} height={20} overflow="visible">
              <PlaceholderLandmark
                x={10}
                y={10}
                radius={8}
                habitat={habitat}
                cardId={`${habitat}_L`}
              />
            </svg>
            {habitat.charAt(0).toUpperCase() + habitat.slice(1)}
          </button>
        ))}
      </div>

      {filteredCards.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filteredCards.slice(0, 3).map((card) => (
            <button
              key={card.cardId}
              data-testid={`cascadia-landmark-card-${card.cardId}`}
              data-action-type="ChooseLandmarkCard"
              onClick={() => onChooseCard(card.cardId)}
              type="button"
              style={{
                minHeight: 44,
                padding: 8,
                border: "1px solid var(--cn-cascadia-market-slot-border)",
                borderRadius: 6,
                background: "var(--cn-cascadia-market-slot)",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <svg width={180} height={100} overflow="visible">
                <PlaceholderCard kind="landmark" card={card} width={180} />
              </svg>
            </button>
          ))}
        </div>
      ) : null}

      <button
        data-testid="cascadia-landmark-skip-btn"
        data-action-type="SkipLandmark"
        onClick={onSkip}
        type="button"
        style={{
          minHeight: 44,
          padding: "8px 16px",
          borderRadius: 6,
          border: "1px solid var(--cn-cascadia-tile-outline)",
          background: "transparent",
          cursor: "pointer",
          fontSize: 13,
          color: "var(--cn-cascadia-label)",
        }}
      >
        Skip / 건너뛰기
      </button>
    </section>
  );
}
